<template>
	<div
		v-if="hasTextContent"
		ref="contextMenu"
		class="v2-context-menu"
		:class="{
			'is-open' : isOpen,
			'is-hovered': isHovered,
			'is-transitioning': isTransitioning,
			[`-${computedAlign.split('-')[0]}`]: true,
			[`-${computedAlign.split('-')[1]}`]: true,
			'open-on-hover': openOnHover,
			'-full-width': fullWidth,
		}"
		:style="delay > 0 ? { '--context-menu-delay': `${delay}ms` } : {}"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<div
			class="v2-context-menu__toggle"
			:class="{
				'-arrow': arrow,
			}"
			@click.prevent="toggleContextMenu"
		>
			<slot name="toggle" />
		</div>
		<div
			ref="contextMenuBody"
			class="v2-context-menu__body"
			:class="{
				'-arrow': arrow,
				[`-${computedAlign}`]: true
			}"
			@transitionend="handleTransitionEnd"
		>
			<div
				class="v2-context-menu__body-inner"
				:style="{'max-height': maxHeight}"
			>
				<slot name="context-menu" />
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name:  'ContextMenu',
	props: {
		keepOpen: {
			type:    Boolean,
			default: true,
		},
		openOnHover: {
			type:    Boolean,
			default: false,
		},
		arrow: {
			type:    Boolean,
			default: true,
		},
		align: {
			type:      String,
			default:   'bottom-right',
			validator: align => ['bottom-right', 'bottom-left', 'bottom-center', 'top-right', 'top-left', 'top-center'].includes(align),
		},
		maxHeight: {
			type:    String,
			default: '60vh',
		},
		fullWidth: {
			type:    Boolean,
			default: false,
		},
		delay: {
			type:    Number,
			default: 0,
		},
	},
	data() {
		return {
			isOpen:          false,
			hasTextContent:  true,
			computedAlign:   this.align,
			isMounted:       false,
			isHovered:       false,
			isTransitioning: false,
			openTimeout:     null,
		};
	},
	watch: {
		isOpen(value) {
			this.$emit('change', value);
			document[(value ? 'add' : 'remove') + 'EventListener']('click', this.onClickOutside);
		},
		align: {
			handler(newAlign) {
				this.computedAlign = newAlign;
			},
			immediate: true,
		},
	},
	mounted() {
		this.isMounted = true;
		this.loadMenu();
		setTimeout(() => {
			// waiting for the content to be loaded
			// not perfect but works for now
			if (this.isMounted) {
				this.autoAlign();
			}
		}, 500);

		// listen for a close event on the event bus
		this.eventBus?.$on('context-menu:close', this.close);
	},
	beforeUnmount() {
		this.isMounted = false;
		document.removeEventListener('click', this.onClickOutside);

		// Clear any pending timeout
		if (this.openTimeout) {
			clearTimeout(this.openTimeout);
		}
	},
	methods: {
		loadMenu() {
			this.hasTextContent = this.$refs.contextMenuBody?.textContent?.trim()?.length !== 0 ?? false;
		},
		toggleContextMenu() {
			if (this.openOnHover) {
				return;
			}

			// Auto-align before opening to ensure proper positioning
			if (!this.isOpen) {
				this.autoAlign();
			}

			this.isOpen = !this.isOpen;
		},
		onClickOutside(event) {
			if (!this.$el.contains(event.target)) {
				this.isOpen = false;
			}
		},

		handleMouseEnter() {
			if (!this.openOnHover) {
				return;
			}

			// Clear any existing timeout
			if (this.openTimeout) {
				clearTimeout(this.openTimeout);
			}

			// Set timeout to open after delay
			this.openTimeout = setTimeout(() => {
				// Auto-align before opening to ensure proper positioning
				this.autoAlign();
				this.isHovered = true;
			}, this.delay);
		},
		handleMouseLeave() {
			if (!this.openOnHover) {
				if (this.keepOpen) {
					return;
				}
				this.isOpen = false;
				return;
			}

			// Clear any existing timeout
			if (this.openTimeout) {
				clearTimeout(this.openTimeout);
				this.openTimeout = null;
			}

			// Set transitioning state and close
			this.isTransitioning = true;
			this.isHovered = false;
		},
		handleTransitionEnd(event) {
			// Only handle opacity transitions
			if (event.propertyName === 'opacity') {
				this.isTransitioning = false;

				// Reset alignment when menu is fully closed (opacity transition ended)
				if (!this.isOpen && !this.isHovered) {
					this.computedAlign = this.align;
				}
			}
		},
		close() {
			this.isOpen = false;
		},
		autoAlign() {
			if (!this.$refs.contextMenuBody) {
				return;
			}

			// Temporarily make the body visible to calculate its dimensions
			const originalDisplay = this.$refs.contextMenuBody.style.display;
			this.$refs.contextMenuBody.style.display = 'block';
			this.$refs.contextMenuBody.style.visibility = 'hidden';
			this.$refs.contextMenuBody.style.position = 'absolute';

			// Force a reflow to ensure dimensions are calculated
			// eslint-disable-next-line no-unused-expressions
			this.$refs.contextMenuBody.offsetHeight;

			const { top, left, bottom, right } = this.$refs.contextMenuBody.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const viewportWidth = window.innerWidth;

			// Check if the menu would be outside the viewport
			const isOutsideTop = top < 0;
			const isOutsideLeft = left < 0;
			const isOutsideBottom = bottom > viewportHeight;
			const isOutsideRight = right > viewportWidth;

			// Reset to original state
			this.$refs.contextMenuBody.style.display = originalDisplay;
			this.$refs.contextMenuBody.style.visibility = '';
			this.$refs.contextMenuBody.style.position = '';

			// Adjust alignment if needed
			if (isOutsideTop) {
				this.computedAlign = this.computedAlign.replace('top', 'bottom');
			}
			if (isOutsideBottom) {
				this.computedAlign = this.computedAlign.replace('bottom', 'top');
			}
			if (isOutsideLeft) {
				this.computedAlign = this.computedAlign.replace('right', 'left');
				if (this.computedAlign.includes('center')) {
					this.computedAlign = this.computedAlign.replace('center', 'left');
				}
			}
			if (isOutsideRight) {
				this.computedAlign = this.computedAlign.replace('left', 'right');
				if (this.computedAlign.includes('center')) {
					this.computedAlign = this.computedAlign.replace('center', 'right');
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$arrow-vertical-offset: 10px;
$arrow-size: 10px;
$hover-protect-area-offset: 35px;

.v2-context-menu {
	position: relative;
	display: inline-flex;
	align-items: center;
	z-index: 1;

	&.-full-width {
		width: 100%;
	}

	&.is-hovered,
	&.is-open,
	&.is-transitioning {
		@include z-index(manager-context-menu);
	}
}

.v2-context-menu__toggle {
	// Keep the trigger clickable where the body's transparent hover bridge overlaps it.
	z-index: 2;
	text-align: center;
	display: flex;
	align-items: center;
	cursor: pointer;

	.v2-context-menu.-full-width & {
		width: 100%;
	}

	&.-arrow {
		&::before {
			@include z-index(manager-context-menu);
			opacity: 0;
			transition: opacity $trans-time-fast;
			content: '';
			position: absolute;
			border: $arrow-size solid;
			border-color: transparent transparent var(--color-white);
			left: calc(50% - #{$arrow-size});
			bottom: -#{$arrow-vertical-offset};

			.v2-context-menu.-top & { // stylelint-disable-line
				border-color: var(--color-white) transparent transparent;
				top: -#{$arrow-vertical-offset};
				bottom: auto;
			}
		}

		// Shadow for arrow (is below arrow itself)
		&::after {
			opacity: 0;
			transition: opacity $trans-time-fast;
			content: '';
			position: absolute;
			border: 10px solid;
			border-color: transparent transparent var(--color-white);
			left: calc(50% - #{$arrow-size});
			bottom: -#{$arrow-vertical-offset};
			filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));

			.v2-context-menu.-top & { // stylelint-disable-line max-nesting-depth
				border-color: var(--color-white) transparent transparent;
				top: -#{$arrow-vertical-offset};
				bottom: auto;
			}
		}

		.v2-context-menu.is-hovered &,
		.v2-context-menu.is-open & {
			&::before { // stylelint-disable-line max-nesting-depth
				opacity: 1;
			}

			&::after { // stylelint-disable-line max-nesting-depth
				opacity: 1;
			}
		}
	}
}

// arrow hover background on first item hover
.v2-context-menu:has(.context-menu .context-menu__item:not(.context-menu__item ~ .context-menu__item):hover) .v2-context-menu__toggle.-arrow::before {
	border-color: transparent transparent var(--color-soft-white);
}

.v2-context-menu.-top:has(.context-menu .context-menu__item:not(.context-menu__item ~ .context-menu__item):hover) .v2-context-menu__toggle.-arrow::before {
	border-color: var(--color-soft-white) transparent transparent;
}

.v2-context-menu:has(.context-menu .context-menu__item:not(.context-menu__item ~ .context-menu__item):hover) .v2-context-menu__toggle.-arrow::after {
	border-color: transparent transparent var(--color-soft-white);
}

.v2-context-menu.-top:has(.context-menu .context-menu__item:not(.context-menu__item ~ .context-menu__item):hover) .v2-context-menu__toggle.-arrow::after {
	border-color: var(--color-soft-white) transparent transparent;
}

.v2-context-menu__body {
	$arrow-size: 10px;
	$arrow-offset: 20px;

	position: absolute;
	filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));
	text-align: center;
	background-color: var(--color-white);
	z-index: 1;
	opacity: 0;
	pointer-events: none;
	border-radius: var(--t-border-radius--lg);
	// The hover bridge must extend outside the body; clip content in the inner container.
	overflow: visible;
	transition: opacity $trans-time-fast;

	// Cover the trigger-side strip across the menu width for horizontal/diagonal entry.
	&::before {
		content: '';
		position: absolute;
		top: -#{$hover-protect-area-offset};
		left: 0;
		width: 100%;
		height: $hover-protect-area-offset;
		z-index: -1;

		.v2-context-menu.-top & {
			top: auto;
			bottom: -#{$hover-protect-area-offset};
		}
	}

	.v2-context-menu.-bottom & {
		top: 100%;
	}

	.v2-context-menu.-top & {
		bottom: 100%;
	}

	.v2-context-menu.-left & {
		left: 0;
	}

	.v2-context-menu.-right & {
		right: 0;
	}

	.v2-context-menu.-center & {
		left: 50%;
		transform: translateX(-50%);
	}

	&.-arrow {
		.v2-context-menu.-bottom & {
			top: calc(100% + #{$arrow-vertical-offset});
		}

		.v2-context-menu.-top & {
			bottom: calc(100% + #{$arrow-vertical-offset});
		}

		.v2-context-menu.-left & {
			left: -8px;
		}

		.v2-context-menu.-right & {
			right: -8px;
		}
	}

	.v2-context-menu.is-hovered &,
	.v2-context-menu.is-open & {
		opacity: 1;
		pointer-events: initial;
	}
}

.v2-context-menu__body-inner {
	border-radius: inherit;
	background-color: var(--color-white);
	overflow: auto;
}
</style>
